import {
  rollDice, allocateDice, useAbility, buyCard, skipBuy, claimCard, endTurn, declineClaim, declineExtraBuy,
  resolveArrowChoice, placeSetupCard, resolveSectorPick, resolveDeckPeek, declineSectorPick, resolveChargeChoice,
  resolveMoveChoice, resolveCardSwapChoice, resolveEmptySectorChoice, resolveCardGiftTarget, resolveBonusRollChoice,
  resolveNeighborChoice, declineEmptySectorChoice, resolveShipDiscard, resolveDieRewardChoice, declineDieRewardChoice,
  resolveCardTradeChoice,
} from './turnActions';
import { defFor } from '../cards/cardHelpers';

export const ACTION_TYPES = {
  ROLL_DICE: 'ROLL_DICE',
  ALLOCATE_DICE: 'ALLOCATE_DICE',
  USE_ABILITY: 'USE_ABILITY',
  BUY_CARD: 'BUY_CARD',
  SKIP_BUY: 'SKIP_BUY',
  CLAIM_CARD: 'CLAIM_CARD',
  END_TURN: 'END_TURN',
  DECLINE_CLAIM: 'DECLINE_CLAIM',
  DECLINE_EXTRA_BUY: 'DECLINE_EXTRA_BUY',
  RESOLVE_ARROW_CHOICE: 'RESOLVE_ARROW_CHOICE',
  PLACE_SETUP_CARD: 'PLACE_SETUP_CARD',
  RESOLVE_SECTOR_PICK: 'RESOLVE_SECTOR_PICK',
  RESOLVE_DECK_PEEK: 'RESOLVE_DECK_PEEK',
  DECLINE_SECTOR_PICK: 'DECLINE_SECTOR_PICK',
  RESOLVE_CHARGE_CHOICE: 'RESOLVE_CHARGE_CHOICE',
  RESOLVE_MOVE_CHOICE: 'RESOLVE_MOVE_CHOICE',
  RESOLVE_CARD_SWAP: 'RESOLVE_CARD_SWAP',
  RESOLVE_EMPTY_SECTOR_CHOICE: 'RESOLVE_EMPTY_SECTOR_CHOICE',
  RESOLVE_CARD_GIFT_TARGET: 'RESOLVE_CARD_GIFT_TARGET',
  RESOLVE_BONUS_ROLL: 'RESOLVE_BONUS_ROLL',
  RESOLVE_NEIGHBOR_CHOICE: 'RESOLVE_NEIGHBOR_CHOICE',
  DECLINE_EMPTY_SECTOR_CHOICE: 'DECLINE_EMPTY_SECTOR_CHOICE',
  RESOLVE_SHIP_DISCARD: 'RESOLVE_SHIP_DISCARD',
  RESOLVE_DIE_REWARD_CHOICE: 'RESOLVE_DIE_REWARD_CHOICE',
  DECLINE_DIE_REWARD_CHOICE: 'DECLINE_DIE_REWARD_CHOICE',
  RESOLVE_CARD_TRADE: 'RESOLVE_CARD_TRADE',
};

function activePlayerName(state) {
  return state.players[state.meta.activePlayerIndex]?.name || 'Someone';
}

function playerName(state, playerId) {
  return state.players.find((p) => p.id === playerId)?.name || 'Someone';
}

// Matt reported this directly: "The log... should have every single
// state change included so I wanna know when I get a dollar or a point
// or a planet. I wanna know when my opponents get those things - should
// just include everything." The top-level describeAction message below
// only ever describes the ACTION itself ("allocated the dice as the
// sum"), not its actual resource consequences - and those consequences
// aren't confined to the acting player either (allOpponentsLoseVP hits
// everyone at once). Diffing every player's credits/income/vp between
// before and after state, generically, catches every resource-granting
// path (dice hits, ability bonus rewards, the endTurn income step) with
// no per-action special-casing. Gains only (not spending a purchase's
// cost down to 0, or an allOpponentsLoseVP loss) - matches what Matt
// actually asked to see ("when I get..."), and avoids a redundant
// "-5 credits" entry sitting right next to the "bought X" entry that
// already explains the same spend.
function describeResourceGains(state, nextState, turnNumber) {
  const entries = [];
  nextState.players.forEach((after) => {
    const before = state.players.find((p) => p.id === after.id);
    if (!before) return;
    const parts = [];
    const creditsGained = after.credits - before.credits;
    const incomeGained = after.income - before.income;
    const vpGained = after.vp - before.vp;
    if (creditsGained > 0) parts.push(`${creditsGained} credit${creditsGained === 1 ? '' : 's'}`);
    if (incomeGained > 0) parts.push(`${incomeGained} income`);
    if (vpGained > 0) parts.push(`${vpGained} VP`);
    if (!parts.length) return;
    entries.push({ turnNumber, playerId: after.id, message: `${after.name} gained ${parts.join(', ')}.` });
  });
  return entries;
}

// Matt reported this directly: "I want to see total earnings [for]
// everybody" (on the endgame stats screen) - not recoverable from final
// credits alone once any get spent, so accumulated turn by turn here,
// same before/after diff as describeResourceGains above.
function accumulateCreditsEarned(state, nextState) {
  // Same "game saved before this field existed" guard as
  // turnActions.js's own recordDiceRolls.
  const totals = { ...(nextState.meta.stats || {}).totalCreditsEarned };
  nextState.players.forEach((after) => {
    const before = state.players.find((p) => p.id === after.id);
    if (!before) return;
    const gained = after.credits - before.credits;
    if (gained > 0) totals[after.id] = (totals[after.id] || 0) + gained;
  });
  return totals;
}

function describeAction(state, nextState, type, payload) {
  switch (type) {
    case ACTION_TYPES.ROLL_DICE: {
      const [d1, d2] = nextState.meta.dice?.values || [];
      return `${activePlayerName(state)} rolled ${d1} and ${d2}.`;
    }
    case ACTION_TYPES.ALLOCATE_DICE:
      return `${playerName(state, payload.playerId)} allocated the dice as ${payload.mode === 'sum' ? 'the sum' : 'separate dice'}.`;
    case ACTION_TYPES.USE_ABILITY: {
      const player = state.players.find((p) => p.id === payload.playerId);
      const found = player?.sectors && Object.values(player.sectors).find((s) => (
        s.station?.instanceId === payload.instanceId || s.deployedStack.some((d) => d.instanceId === payload.instanceId)
      ));
      const instance = found?.station?.instanceId === payload.instanceId
        ? found.station
        : found?.deployedStack.find((d) => d.instanceId === payload.instanceId);
      const name = instance ? defFor(instance.cardId).name : 'a card';
      return `${playerName(state, payload.playerId)} used ${name}'s ability.`;
    }
    case ACTION_TYPES.BUY_CARD: {
      const who = payload.playerId ? playerName(state, payload.playerId) : activePlayerName(state);
      // Biodome's Ion Outpost tap - a buy that names another player's
      // colony without acquiring any card of its own.
      if (payload.ionTapColonyCardId) {
        return `${who} paid ${defFor(payload.ionTapColonyCardId).name}'s cost to gain its reward (Ion Outpost).`;
      }
      const name = payload.colonyCardId ? defFor(payload.colonyCardId).name : defFor(payload.cardId).name;
      return `${who} bought ${name}.`;
    }
    case ACTION_TYPES.SKIP_BUY:
      return `${activePlayerName(state)} chose not to buy a card.`;
    case ACTION_TYPES.CLAIM_CARD:
      return `${playerName(state, payload.playerId)} claimed ${defFor(payload.cardId).name} for free.`;
    case ACTION_TYPES.END_TURN:
      return `${activePlayerName(state)} ended their turn.`;
    case ACTION_TYPES.DECLINE_CLAIM:
      return `${playerName(state, payload.playerId)} declined a free claim (nothing left to claim).`;
    case ACTION_TYPES.DECLINE_EXTRA_BUY:
      return `${playerName(state, payload.playerId)} declined an extra buy.`;
    case ACTION_TYPES.DECLINE_SECTOR_PICK:
      return `${playerName(state, payload.playerId)} declined a sector pick (not enough legal sectors left).`;
    case ACTION_TYPES.RESOLVE_ARROW_CHOICE: {
      const choice = state.meta.pendingArrowChoices?.[payload.choiceIndex];
      const direction = choice && payload.chosenSector < choice.sourceSector ? 'left' : 'right';
      return `${playerName(state, payload.playerId)} chose the sector to their ${direction}.`;
    }
    case ACTION_TYPES.PLACE_SETUP_CARD: {
      const placement = state.meta.pendingSetupPlacements?.find((p) => p.playerId === payload.playerId);
      const name = placement ? defFor(placement.cardId).name : 'their starting ship';
      return `${playerName(state, payload.playerId)} deployed ${name} into sector ${payload.sectorOverride}.`;
    }
    case ACTION_TYPES.RESOLVE_SECTOR_PICK:
      return `${playerName(state, payload.playerId)} picked sectors ${payload.chosenSectors.join(', ')} to gain rewards from.`;
    case ACTION_TYPES.RESOLVE_DECK_PEEK: {
      const claimed = payload.decisions.filter((d) => d.claim).map((d) => defFor(d.cardId).name);
      return claimed.length
        ? `${playerName(state, payload.playerId)} claimed ${claimed.join(' and ')} from the deck.`
        : `${playerName(state, payload.playerId)} put both cards back.`;
    }
    case ACTION_TYPES.RESOLVE_CHARGE_CHOICE:
      return payload.targets.length
        ? `${playerName(state, payload.playerId)} placed a charge on ${payload.targets.length} card${payload.targets.length === 1 ? '' : 's'}.`
        : `${playerName(state, payload.playerId)} chose not to place any charges.`;
    case ACTION_TYPES.RESOLVE_MOVE_CHOICE:
      return `${playerName(state, payload.playerId)} moved a deployed card into sector ${payload.destinationSector}.`;
    case ACTION_TYPES.RESOLVE_CARD_SWAP:
      return `${playerName(state, payload.playerId)} exchanged a card between sectors ${payload.sectorA} and ${payload.sectorB}.`;
    case ACTION_TYPES.RESOLVE_EMPTY_SECTOR_CHOICE:
      return `${playerName(state, payload.playerId)} deployed the ship card in sector ${payload.sector}.`;
    case ACTION_TYPES.RESOLVE_CARD_GIFT_TARGET:
      return `${playerName(state, payload.playerId)} gave ${playerName(state, payload.targetPlayerId)} a drawn card to claim or discard.`;
    case ACTION_TYPES.RESOLVE_BONUS_ROLL: {
      const entry = state.meta.pendingBonusRolls?.[0];
      const [d1, d2] = entry?.values || [];
      return `${playerName(state, payload.playerId)} allocated their Bonus Roll (${d1} and ${d2}) as ${payload.mode === 'sum' ? 'the sum' : 'separate dice'}.`;
    }
    case ACTION_TYPES.RESOLVE_NEIGHBOR_CHOICE:
      return `${playerName(state, payload.playerId)} chose to gain rewards from ${playerName(state, payload.chosenPlayerId)}'s board.`;
    case ACTION_TYPES.DECLINE_EMPTY_SECTOR_CHOICE:
      return `${playerName(state, payload.playerId)} chose not to deploy a ship card.`;
    case ACTION_TYPES.RESOLVE_SHIP_DISCARD:
      return `${playerName(state, payload.playerId)} discarded a ship and claimed the top deck card.`;
    case ACTION_TYPES.RESOLVE_DIE_REWARD_CHOICE:
      return `${playerName(state, payload.playerId)} also gained rewards for the die showing ${payload.sector}.`;
    case ACTION_TYPES.DECLINE_DIE_REWARD_CHOICE:
      return `${playerName(state, payload.playerId)} passed on the extra die reward.`;
    case ACTION_TYPES.RESOLVE_CARD_TRADE:
      return `${playerName(state, payload.playerId)} handed a card across the table.`;
    default:
      return `${activePlayerName(state)} took an action.`;
  }
}

export function dispatch(state, action) {
  // Matt reported this directly: "Even though the game is over, I'm
  // still able to roll the dice and keep going. We should make sure to
  // prevent that." A no-op once a winner is locked in (winnerIds) or an
  // instant-win ability fired (instantWinnerId) - same "return state
  // unchanged" convention every other illegal-move guard in this file
  // already uses, so it's a silent no-op rather than a thrown error.
  if (state.meta.winnerIds || state.meta.instantWinnerId) return state;

  const { type, payload = {} } = action;
  let nextState;
  switch (type) {
    case ACTION_TYPES.ROLL_DICE:
      nextState = rollDice(state);
      break;
    case ACTION_TYPES.ALLOCATE_DICE:
      nextState = allocateDice(state, payload.playerId, payload.mode);
      break;
    case ACTION_TYPES.USE_ABILITY:
      nextState = useAbility(state, payload.playerId, payload.instanceId, payload.slot, payload.params);
      break;
    case ACTION_TYPES.BUY_CARD:
      nextState = buyCard(state, payload);
      break;
    case ACTION_TYPES.SKIP_BUY:
      nextState = skipBuy(state);
      break;
    case ACTION_TYPES.CLAIM_CARD:
      nextState = claimCard(state, payload);
      break;
    case ACTION_TYPES.END_TURN:
      nextState = endTurn(state);
      break;
    case ACTION_TYPES.DECLINE_CLAIM:
      nextState = declineClaim(state, payload);
      break;
    case ACTION_TYPES.DECLINE_EXTRA_BUY:
      nextState = declineExtraBuy(state, payload);
      break;
    case ACTION_TYPES.DECLINE_SECTOR_PICK:
      nextState = declineSectorPick(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_ARROW_CHOICE:
      nextState = resolveArrowChoice(state, payload);
      break;
    case ACTION_TYPES.PLACE_SETUP_CARD:
      nextState = placeSetupCard(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_SECTOR_PICK:
      nextState = resolveSectorPick(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_DECK_PEEK:
      nextState = resolveDeckPeek(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_CHARGE_CHOICE:
      nextState = resolveChargeChoice(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_MOVE_CHOICE:
      nextState = resolveMoveChoice(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_CARD_SWAP:
      nextState = resolveCardSwapChoice(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_EMPTY_SECTOR_CHOICE:
      nextState = resolveEmptySectorChoice(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_CARD_GIFT_TARGET:
      nextState = resolveCardGiftTarget(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_BONUS_ROLL:
      nextState = resolveBonusRollChoice(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_NEIGHBOR_CHOICE:
      nextState = resolveNeighborChoice(state, payload);
      break;
    case ACTION_TYPES.DECLINE_EMPTY_SECTOR_CHOICE:
      nextState = declineEmptySectorChoice(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_SHIP_DISCARD:
      nextState = resolveShipDiscard(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_DIE_REWARD_CHOICE:
      nextState = resolveDieRewardChoice(state, payload);
      break;
    case ACTION_TYPES.DECLINE_DIE_REWARD_CHOICE:
      nextState = declineDieRewardChoice(state, payload);
      break;
    case ACTION_TYPES.RESOLVE_CARD_TRADE:
      nextState = resolveCardTradeChoice(state, payload);
      break;
    default:
      throw new Error(`Unknown action type: ${type}`);
  }

  if (nextState === state) return state; // no-op (illegal move) - no log entry

  const message = describeAction(state, nextState, type, payload);
  const actorId = payload.playerId || state.players[state.meta.activePlayerIndex]?.id;
  const gainEntries = describeResourceGains(state, nextState, state.meta.turnNumber);
  return {
    ...nextState,
    meta: {
      ...nextState.meta,
      log: [...nextState.meta.log, {
        turnNumber: state.meta.turnNumber, playerId: actorId, message,
      }, ...gainEntries],
      stats: { ...nextState.meta.stats, totalCreditsEarned: accumulateCreditsEarned(state, nextState) },
    },
  };
}
